import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import confetti from 'canvas-confetti';
import { X, ChevronRight, ChevronLeft } from 'lucide-react';
import { useBean } from '../context/BeanContext';
import { useLanguage } from '../functions/useLanguage';
import { type TranslationKey } from '../functions/i18n';
import { useRecordsTab } from '../context/RecordsTabContext';
import { usePet } from '../context/PetContext';
import WalkthroughKoda from './WalkthroughKoda';
import { cn } from '../lib/utils';

// ─── Step definitions ────────────────────────────────────────────────────────

type IntroStep   = { kind: 'intro';   expression: 0 };
type FeatureStep = { kind: 'feature'; route: string; target: string; expression: 1 | 2 | 3 | 4 | 5; tab?: string };
type StepDef     = IntroStep | FeatureStep;

const STEPS: StepDef[] = [
  { kind: 'intro',   expression: 0 },
  { kind: 'feature', route: '/vitals',        target: 'today-card',        expression: 1, tab: 'dashboard' },
  { kind: 'feature', route: '/vitals',        target: 'trends-chart',      expression: 2, tab: 'trends' },
  { kind: 'feature', route: '/meds',          target: 'meds-list',         expression: 3 },
  { kind: 'feature', route: '/library',       target: 'guide-resources',   expression: 4 },
  { kind: 'feature', route: '/kodafellowship',target: 'streak-card',       expression: 5 },
];

const FEATURE_COUNT = STEPS.filter(s => s.kind === 'feature').length; // 5

// ─── Props ───────────────────────────────────────────────────────────────────

interface WalkthroughProps {
  onDismiss: () => void;
}

// ─── Component ───────────────────────────────────────────────────────────────

export function Walkthrough({ onDismiss }: WalkthroughProps) {
  const navigate  = useNavigate();
  const { t }     = useLanguage();
  const { awardXP } = useBean();
  const { setTab } = useRecordsTab();

  const [stepIdx,   setStepIdx]   = useState(0);
  const [visible,   setVisible]   = useState(false);
  const [isMobile,  setIsMobile]  = useState(window.innerWidth < 1024);
  const [showXP,    setShowXP]    = useState(false);
  const [petName,   setPetName]   = useState('');
  const [petNameSaved, setPetNameSaved] = useState(false);
  const [savingName,   setSavingName]   = useState(false);
  const { activePet, updatePet, createPet, petsLoading } = usePet();

  // Track viewport size
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // ── Step navigation ──────────────────────────────────────────────────────

  const gotoStep = useCallback(async (i: number) => {
    setVisible(false);

    const step = STEPS[i];
    if (step.kind === 'feature') {
      navigate(step.route);
      if (step.tab) setTab(step.tab as any);
      await new Promise<void>(r => setTimeout(r, 420));
    }

    setStepIdx(i);
    setVisible(true);
  }, [navigate, setTab]);

  // Start on mount
  useEffect(() => {
    gotoStep(0);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Dismiss / finish ─────────────────────────────────────────────────────

  const dismiss = useCallback(() => {
    localStorage.setItem('koda_walkthrough_shown', '1');
    onDismiss();
  }, [onDismiss]);

  const finish = useCallback(() => {
    localStorage.setItem('koda_walkthrough_shown', '1');
    awardXP('onboarding');
    setShowXP(true);
    // burst confetti from centre
    confetti({ particleCount: 120, spread: 80, origin: { y: 0.55 }, colors: ['#C17A2E','#F5C57A','#fff','#E8A84C'] });
    setTimeout(() => confetti({ particleCount: 60, spread: 120, origin: { y: 0.45 }, colors: ['#C17A2E','#F5C57A','#fff'] }), 250);
    // navigate to Vitals Today tab after animation
    setTimeout(() => {
      setShowXP(false);
      setTab('dashboard');
      navigate('/records');
      onDismiss();
    }, 2200);
  }, [awardXP, setTab, navigate, onDismiss]);

  const handleSavePetName = async () => {
    if (!petName.trim()) return;
    setSavingName(true);
    try {
      if (activePet?.id) {
        await updatePet(activePet.id, { name: petName.trim() });
      } else {
        // No pet yet (e.g. pet context still loading or not seeded) — create one
        await createPet({ name: petName.trim(), species: 'cat', color: '#C17A2E' });
      }
    } catch { /* non-blocking */ }
    setSavingName(false);
    setPetNameSaved(true);
  };

  const handleNext = async () => {
    if (stepIdx < STEPS.length - 1) {
      await gotoStep(stepIdx + 1);
    } else {
      finish();
    }
  };

  const handleBack = async () => {
    if (stepIdx > 0) await gotoStep(stepIdx - 1);
  };


  const step       = STEPS[stepIdx];
  // Steps 1 & 2 navigate to /records — the Records tab bar is visible, so lift the bubble above it
  const showsTabBar = step.kind === 'feature' && step.route === '/records';
  const mobileBottom = showsTabBar ? 'bottom-[120px]' : 'bottom-[76px]';

  const stepKey = (suffix: string) => (`walkthrough.step${stepIdx}.${suffix}`) as TranslationKey;

  // ── Shared dots + button ─────────────────────────────────────────────────

  const dots = (
    <div className="flex items-center gap-1.5">
      {STEPS.map((_, i) => (
        <div
          key={i}
          className="rounded-full transition-all duration-300"
          style={{
            width:      i === stepIdx ? 18 : 6,
            height:     6,
            background: i === stepIdx ? '#C17A2E' : '#C17A2E33',
          }}
        />
      ))}
    </div>
  );

  const nextBtn = (
    <button
      onClick={handleNext}
      disabled={stepIdx === 0 && !petNameSaved}
      className="flex items-center gap-1.5 px-5 py-2 rounded-full bg-primary-brown text-white text-sm font-bold hover:opacity-90 active:scale-95 transition-all disabled:opacity-30 disabled:cursor-not-allowed disabled:active:scale-100"
    >
      {stepIdx === 0
        ? <>{t('walkthrough.letsGo')}<ChevronRight size={14} /></>
        : stepIdx < STEPS.length - 1
          ? <>{t('walkthrough.next')}<ChevronRight size={14} /></>
          : t('walkthrough.finish')}
    </button>
  );

  const backBtn = stepIdx > 0 ? (
    <button
      onClick={handleBack}
      className="flex items-center gap-1 text-sm font-semibold text-text-secondary hover:text-primary-brown transition-colors"
    >
      <ChevronLeft size={14} />{t('walkthrough.back')}
    </button>
  ) : null;

  const actionRow = (
    <div className="flex items-center justify-between w-full">
      <div className="flex items-center gap-3">
        {dots}
        {backBtn}
      </div>
      {nextBtn}
    </div>
  );

  // ── Intro card (step 0) ───────────────────────────────────────────────────

  const introCard = (
    <div
      className={cn(
        'bg-white rounded-3xl shadow-2xl transition-all duration-300 z-[301]',
        visible ? 'opacity-100 scale-100' : 'opacity-0 scale-95 pointer-events-none',
        isMobile
          ? `fixed left-3 right-3 ${mobileBottom}`
          : 'fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[340px]',
      )}
    >
      <button
        onClick={dismiss}
        aria-label={t('walkthrough.skip')}
        className="absolute top-4 right-4 w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors z-10"
      >
        <X size={14} className="text-gray-500" />
      </button>

      <div className="px-6 pt-6 pb-5 flex flex-col items-center text-center">
        <div className="overflow-visible mb-2" style={{ width: 110 }}>
          <WalkthroughKoda expression={0} size={110} />
        </div>
        <p className="text-[10px] font-extrabold text-primary-brown uppercase tracking-widest mb-1">
          {t('walkthrough.polyName')}
        </p>
        <h3 className="font-black text-highlight-dark text-xl leading-tight mb-2">
          {t('walkthrough.step0.title')}
        </h3>
        <p className="text-sm text-gray-600 leading-relaxed mb-4">
          Pleasure to meet you. I've prepared a welcome gift of <span className="font-bold text-primary-brown">+30 XP</span> for you! More about that later… What's your pet's name?
        </p>

        {/* Pet name input + save */}
        {!petNameSaved ? (
          <div className="w-full mb-5">
            <div className="flex gap-2">
              <input
                type="text"
                value={petName}
                onChange={e => setPetName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && petName.trim()) handleSavePetName(); }}
                placeholder={activePet?.name || 'e.g. Buddy'}
                className="flex-1 px-3 py-2 rounded-xl border border-slate-200 text-[#0d2744] font-semibold outline-none focus:ring-2 focus:ring-primary-brown/30 text-sm"
                autoFocus
              />
              <button
                onClick={handleSavePetName}
                disabled={!petName.trim() || savingName || petsLoading}
                className="px-4 py-2 rounded-xl bg-primary-brown text-white text-sm font-bold hover:opacity-90 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
              >
                {savingName ? '…' : 'Save'}
              </button>
            </div>
          </div>
        ) : (
          <div className="w-full mb-5 px-4 py-3 rounded-xl bg-primary-brown/8 border border-primary-brown/20 text-sm text-primary-brown font-semibold animate-in fade-in duration-300">
            Welcome {petName.trim()}! Let me show you guys around. 🐾
          </div>
        )}

        <div className="flex items-center justify-between w-full">
          {actionRow}
        </div>
      </div>
    </div>
  );

  // ── Feature card (steps 1-5) ──────────────────────────────────────────────

  const featureCard = (
    <div
      className={cn(
        'bg-white rounded-3xl shadow-2xl transition-all duration-300 z-[301]',
        visible ? 'opacity-100 scale-100' : 'opacity-0 scale-95 pointer-events-none',
        isMobile
          ? `fixed left-3 right-3 ${mobileBottom}`
          : 'fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[340px]',
      )}
    >
      <div className="p-5">
        <div className="flex items-start gap-3 mb-3">
          {/* Koda icon — overflow visible so tall SVGs are not clipped */}
          <div className="w-[52px] shrink-0 overflow-visible flex justify-center">
            <WalkthroughKoda expression={step.kind === 'feature' ? step.expression : 1} size={52} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-extrabold text-primary-brown uppercase tracking-widest mb-0.5">
              {t('walkthrough.polyName')} · {stepIdx}/{FEATURE_COUNT}
            </p>
            <h3 className="font-black text-highlight-dark text-base leading-tight">
              {t(stepKey('title'))}
            </h3>
          </div>
          <button
            onClick={dismiss}
            aria-label={t('walkthrough.skip')}
            className="w-7 h-7 shrink-0 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors"
          >
            <X size={14} className="text-gray-500" />
          </button>
        </div>

        <p className="text-sm text-gray-600 mb-4 leading-relaxed">
          {t(stepKey('body'))}
        </p>

        <div className="flex items-center justify-between">
          {actionRow}
        </div>
      </div>
    </div>
  );

  // ── Portal ───────────────────────────────────────────────────────────────

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-[299]"
        style={{ background: 'rgba(0,0,0,0.15)' }}
        onClick={dismiss}
      />
      {step.kind === 'intro' ? introCard : featureCard}

      {/* XP celebration overlay */}
      {showXP && (
        <div className="fixed inset-0 z-[400] flex items-center justify-center pointer-events-none">
          <div
            className="flex flex-col items-center gap-2 animate-[xpPop_0.55s_cubic-bezier(0.34,1.56,0.64,1)_both]"
            style={{ animationFillMode: 'both' }}
          >
            <span className="text-7xl font-black text-primary-brown drop-shadow-lg leading-none">+30 XP</span>
            <span className="text-lg font-bold text-primary-brown/70">🎉 Welcome to KodaCare!</span>
          </div>
        </div>
      )}
    </>,
    document.body,
  );
}
